import { useEffect, useMemo } from 'react'
import { trackEvent } from '../../../../utils/appBridge'
import BottomSheetContent from '../../BottomSheetContent'
import { EventNames } from '../../../../types/misc.types'
import RenderHTML from '../../../ui-components/RenderHTML'
import {
  upiIcon,
  autoPayIcon,
  netBankingIcon,
  investPaymentMethodConfig
} from '../../../../utils/mf-execution/paymentPageConfig'
import { InvestPaymentMethodTypes } from '../../../../types/mf-execution.types'
import { PrimaryButton } from '../../../execution/PrimaryButton'
import { formatIndianNumber } from '../../../../utils/FD/fd'
import { oneTimeInvestIcon } from '../../../../utils/mf-execution/buttonsConfig'
import { CalendarSelection } from '../../../../types/calendar.types'
import { convertEpochToDate, toPascalCaseWithSpaces } from '../../../../utils/utils'

export const SIPConfirmationBottomSheet = ({
  onDismiss,
  ScreenName,
  amount,
  displayAccNumber,
  onPlaceOrder,
  sipDate,
  firstSIPDate,
  nextSIPDate,
  whyNotSIPDate,
  onWhyNotSIPClick,
  upiId,
  paymentMethodIconUrl,
  paymentMethodType,
  frequencyType,
  trackingFund
}: {
  onDismiss: () => void
  ScreenName: string
  amount: number
  displayAccNumber: string
  onPlaceOrder: () => void
  sipDate: string
  firstSIPDate: string
  nextSIPDate: number
  whyNotSIPDate: number | null
  onWhyNotSIPClick: (nextSIPDate: number, whyNotSIPDate: number) => void
  upiId?: string
  paymentMethodIconUrl?: string
  paymentMethodType: InvestPaymentMethodTypes
  frequencyType: CalendarSelection['frequencyType']
  trackingFund?: { fundIsin: string; fundName: string }
}) => {
  const heading = 'Review details'

  const getPaymentMethodIcon = () => {
    switch (paymentMethodType) {
      case InvestPaymentMethodTypes.MANDATE:
        return autoPayIcon
      case InvestPaymentMethodTypes.UPI:
      case InvestPaymentMethodTypes.UPI_INTENT:
        return upiIcon
      case InvestPaymentMethodTypes.NET_BANKING:
        return netBankingIcon
      default:
        return upiIcon
    }
  }

  const whyNotSection = useMemo(() => {
    if (!whyNotSIPDate || !nextSIPDate) return null

    return (
      <>
        &nbsp;&nbsp;{' '}
        <span
          className='text-[#6A6E6E] font-[400] underline cursor-pointer'
          onClick={() => onWhyNotSIPClick?.(nextSIPDate, whyNotSIPDate)}
        >
          {' '}
          Why not {convertEpochToDate(whyNotSIPDate, '%d %b')}?
        </span>
      </>
    )
  }, [whyNotSIPDate, nextSIPDate])

  const getPaymentMethodLabel = () => {
    if (
      (paymentMethodType === InvestPaymentMethodTypes.UPI ||
        paymentMethodType === InvestPaymentMethodTypes.UPI_INTENT) &&
      upiId
    ) {
      return upiId
    }
    return investPaymentMethodConfig?.[paymentMethodType]?.label ?? investPaymentMethodConfig[InvestPaymentMethodTypes.UPI].label
  }

  const shouldShowAutoPayNote = paymentMethodType === InvestPaymentMethodTypes.MANDATE

  useEffect(() => {
    trackEvent({
      eventName: EventNames.BottomSheetLoad,
      payload: {
        bottomSheetName: 'SIP review details',
        ...(trackingFund ?? {})
      }
    })
  }, [])

  return (
    <BottomSheetContent
      heading={heading}
      onDismiss={onDismiss}
      bottomSheetHeadingClassName='!font-[500]'
      bottomSheetBodyClassName='!px-0 !pb-5'
      bottomSheetCtas={
        <PrimaryButton
          onClick={onPlaceOrder}
          label={paymentMethodType === InvestPaymentMethodTypes.MANDATE ? 'Place order' : 'Continue to pay'}
          primaryButtonClassName='!h-[56px] !text-[16px] !font-[500]'
        />
      }
    >
      <div test-locator='MFE_BS_REVIEW_DETAILS' className='flex flex-col pt-6 border-t border-[#F0F4F7]'>
        {/* SIP Details */}
        <div className='flex gap-5 w-full pl-5'>
          <div className='pt-3'>
            <RenderHTML innerHTMLText={oneTimeInvestIcon} />
          </div>
          <div className='flex w-1 flex-1'>
            <div className='flex flex-col'>
              <div className='text-[#8E9393] font-[400] text-[13px]'>SIP amount</div>
              <div className='text-[#232323] text-[22px] font-[500] pb-2 '>{formatIndianNumber(amount, true)}</div>
              <div className='flex flex-col gap-[6px] mt-2'>
                <div className='text-[#6A6E6E] text-[13px] font-[400] pb-[2px] flex items-center'>
                  <div className='w-[75px]'>
                    <span className='text-[#6A6E6E]'>SIP date</span>{' '}
                  </div>
                  <span className='text-[#232323] font-[500]'>{sipDate}</span>
                </div>
                <div className='text-[#6A6E6E] text-[13px] font-[400] pb-[2px] flex items-center'>
                  <div className='w-[75px]'>
                    <span className='text-[#6A6E6E]'>First SIP</span>{' '}
                  </div>
                  <span className='text-[#232323] font-[500]'>{firstSIPDate}</span>
                </div>
                <div className='text-[#6A6E6E] text-[13px] font-[400] pb-[2px] flex items-center'>
                  <div className='w-[75px]'>
                    <span className='text-[#6A6E6E] '>Next SIP</span>{' '}
                  </div>
                  <span className='text-[#232323] font-[500]'>{convertEpochToDate(nextSIPDate, '%d %b %Y')}</span>
                  {whyNotSection}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='pl-16 pt-6 pb-4'>
          <div className='bg-[#F0F4F7] h-[1px] w-full'></div>
        </div>
        {/* Payment Method */}
        <div className='flex items-center gap-[14px] w-full pl-[14px]'>
          <div className='flex-shrink-0'>
            {paymentMethodIconUrl ? (
              <img
                src={paymentMethodIconUrl}
                alt={getPaymentMethodLabel()}
                className='w-10 h-10 rounded-lg object-contain'
              />
            ) : (
              <RenderHTML innerHTMLText={getPaymentMethodIcon()} />
            )}
          </div>
          <div className='flex w-1 flex-1'>
            <div className='flex flex-col w-full'>
              <div className='text-[#232323] text-[14px] font-[500]'>{getPaymentMethodLabel()}</div>
              <div className='text-[#6A6E6E] text-[13px] font-[400] pt-[2px] truncate max-w-[90%]'>{displayAccNumber}</div>
            </div>
          </div>
        </div>
        {/* Auto Pay Note - Only show for Autopay */}
        {shouldShowAutoPayNote && (
          <div
            style={{ background: 'linear-gradient(90deg, #F0F4F7 0%, rgba(240, 244, 247, 0.00) 100%)' }}
            className='text-[#6A6E6E] font-[400] text-[13px] rounded-[8px] px-[8px] py-[4px] pr-10 ml-[68px] mt-3 w-max'
          >
            Auto debited in 1–2 working days
          </div>
        )}
      </div>
    </BottomSheetContent>
  )
}
